import {
  useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import { useTranslation } from 'react-i18next';

import useMaskedInput, { getClean } from 'hooks/useMaskedInput';
import { BoardCrop, BoardSoil } from 'features/Croptimizer/types';
import {
  CropTypes, SoilTypes, TypeOfKey,
} from 'utils/types';
import useCroptimizerSoil from 'api/hooks/useCroptimizerSoil';
import useCroptimizerCrop from 'api/hooks/useCroptimizerCrop';
import { BoardSteps, Metric } from 'utils/constants';
import {
  mtHaToTonAc, mtTot, numericValues, unMaskInput, unMaskInputValue,
} from 'utils/helpers';
import { tableTargetStatus } from 'calculations/german/calculationValues';
import { useNextHandler } from 'features/Croptimizer/providers/NextHandlerContext';
import { useCroptimizer } from 'features/Croptimizer/providers/CroptimizerContext';
import { calcCurrentPriceByCropNSoil } from 'calculations/german/calcCurrentPriceByCropNSoil';
import { getP39Value, yieldPercentHelper } from 'features/Croptimizer/utils/calcHelperBoard';

// INFO
// CrOptimizer-Simplification task
// nv removed from the equation

const nv = '100';
const cleannv = '100';

export const useGermanStatusBoard = () => {
  const {
    setStep,
    setBoardStatusValues,
    setBoardSoilValues,
    boardSoilValues,
    step,
    boardCropValues,
    boardStatusValues,
    setBoardCropValues,
    metricValue,
    kg,
    mtHa,
    efficiency,
  } = useCroptimizer();
  const { t } = useTranslation();
  const isActive = step === BoardSteps.status;
  const { setDisabled, setHandler, setShowBack, setShowNext } =
    useNextHandler();

  const { data: soils } = useCroptimizerSoil();
  const { data: crop } = useCroptimizerCrop();
  const [initialPrice, setInitialPrice] = useState<string>('');

  const changeBoardSoilValues = useCallback(
    (name: keyof BoardSoil) => (value: TypeOfKey<BoardSoil>) => {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-expect-error
      setBoardSoilValues((prevValues: BoardSoil | null) => ({
        ...prevValues,
        [name]: value,
      }));
    },
    [],
  );

  const changeBoardCropValues = useCallback(
    (name: keyof BoardCrop) => (value: TypeOfKey<BoardCrop>) => {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-expect-error
      setBoardCropValues((prevValues: BoardCrop | null) => ({
        ...prevValues,
        [name]: value,
      }));
    },
    [],
  );

  const cropSelect = useMemo(() => {
    if (crop) {
      return crop?.map((crop) => ({
        value: crop.Crops,
        label: crop.Crops,
      }));
    }

    return [];
  }, [crop]);

  const soilSelect = useMemo(() => {
    if (soils) {
      return soils?.map((soil) => ({
        value: soil.Soils,
        label: soil.Soils,
      }));
    }

    return [];
  }, [soils]);

  const isGreaterTarget = useMemo(() => {
    if (boardStatusValues) {
      return boardStatusValues?.currentPh < boardStatusValues?.targetPh;
    }

    return false;
  }, [boardStatusValues]);

  const { value: soilType, setValue: setSoilType } = useMaskedInput({
    defaultValue: soilSelect[0]?.value,
    value: boardSoilValues?.soilType,
    onChangeCallback: (value) => {
      changeBoardSoilValues('soilType')(value);
      setSecSmol(
        soils?.find((soil) => soil.Soils === value)?.CEC.toString() ?? '',
      );
      const isGrassland = cropType === CropTypes.Grassland;
      const newTargetPh =
        tableTargetStatus[
          isGrassland ? CropTypes.Grassland : CropTypes.OtherCrops
        ][value as SoilTypes];

      setTargetPh(getClean(newTargetPh ?? '5,8'));
      setInitialTargetPh(getClean(newTargetPh ?? '5,8'));
      changeBoardSoilValues('totalMtha')(String(Number(getTotalMtha)));
    },
    isDisableClean: true,
  });

  const getCurrentYield = useCallback((value: string) => {
    const getCrop = crop?.find((crops) => crops.Crops === value);
    const resultYield =
      metricValue === Metric.metric
        ? String(getCrop?.PotentialYeild)
        : String(mtHaToTonAc(Number(getCrop?.PotentialYeild)));
    setCurrentYield(unMaskInputValue(resultYield));
    setInitialCurrentYield(unMaskInputValue(resultYield));
  }, [crop]);

  const { value: cropType, setValue: setCropType } = useMaskedInput({
    defaultValue: boardCropValues?.crop ?? cropSelect[0]?.value,
    value: boardCropValues?.crop ?? cropSelect[0]?.value,
    onChangeCallback: (value) => {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-expect-error
      setBoardCropValues((prevValues: BoardCrop | null) => ({
        ...prevValues,
        crop: value,
        currentYield: getCurrentYield(value),
      }));
    },
    isDisableClean: true,
  });

  const {
    value: currentph,
    setValue: setCurrentPh,
    clean: cleancurrentph,
  } = useMaskedInput({
    defaultValue: '4,89',
    value: boardStatusValues?.currentPh,
    onChangeCallback: (value) => {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-expect-error
      setBoardStatusValues((pv) => ({
        ...pv,
        currentPh: getClean(value),
      }));
      changeBoardSoilValues('totalMtha')(String(Number(getTotalMtha)));
    },
  });

  const {
    value: currentSec,
    setValue: setCurrentSec,
    clean: cleancurrentsec,
  } = useMaskedInput({
    defaultValue: '',
    value: boardStatusValues?.currentSec ?? '',
    onChangeCallback: (value) => {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-expect-error
      setBoardStatusValues((pv) => ({
        ...pv,
        currentSec: value,
      }));
    },
  });

  const {
    value: targetSec,
    setValue: setTargetSec,
    clean: cleantargetsec,
  } = useMaskedInput({ defaultValue: '' });

  const {
    value: targetph,
    setValue: setTargetPh,
    clean: cleantargetph,
  } = useMaskedInput({
    defaultValue: '5,8',
    value: boardStatusValues?.targetPh ?? '5,8',
    onChangeCallback: (value) => {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-expect-error
      setBoardStatusValues((pv) => ({
        ...pv,
        targetPh: getClean(value),
      }));
      changeBoardSoilValues('totalMtha')(String(Number(getTotalMtha)));
    },
  });

  const {
    value: secSmol,
    setValue: setSecSmol,
    clean: cleansecsmol,
  } = useMaskedInput({
    defaultValue: '',
    value: boardSoilValues?.secsmol ?? '',
    onChangeCallback: changeBoardSoilValues('secsmol'),
  });

  const { value: initialSecSmol } = useMaskedInput({
    defaultValue: '',
    value: boardSoilValues?.initialSecsmol ?? '',
    onChangeCallback: changeBoardSoilValues('initialSecsmol'),
    isDisableClean: true,
  });

  const {
    value: fineDrySoil,
    setValue: setFineDrySoil,
    clean: cleanfineDrySoil,
  } = useMaskedInput({
    defaultValue:
      metricValue === Metric.metric
        ? '20'
        : unMaskInputValue(String(mtHaToTonAc(20))),
    value:
      boardSoilValues?.fineDrySoil ??
      (metricValue === Metric.metric
        ? '20'
        : unMaskInputValue(String(mtHaToTonAc(20)))),
    onChangeCallback: changeBoardSoilValues('fineDrySoil'),
  });

  const {
    value: currentYield,
    setValue: setCurrentYield,
    clean: cleancurrentyield,
  } = useMaskedInput({
    defaultValue: '',
    value: boardCropValues?.currentYield ?? '',
    onChangeCallback: changeBoardCropValues('currentYield'),
  });

  const {
    value: dose,
    setValue: setDose,
    clean: cleandose,
  } = useMaskedInput({
    defaultValue: '100',
    value: boardSoilValues?.dose ?? '100',
    onChangeCallback: (value) => {
      changeBoardSoilValues('dose')(value);
      changeBoardSoilValues('totalMtha')(String(Number(getTotalMtha)));
    },
  });

  const errorDose = useMemo(() => {
    if (Number(cleandose) < 10) {
      return t('minimumPossibleValue') + ' 10%';
    }

    if (Number(cleandose) > 300) {
      return t('maximumPossibleValue') + ' 300%';
    }

    return null;
  }, [dose]);

  const { value: split, setValue: setSplit } = useMaskedInput({
    value: boardSoilValues?.split ?? '1',
    defaultValue: '1',
    onChangeCallback: changeBoardSoilValues('split'),
    isDisableClean: true,
  });

  const getMthaText = useMemo(() => {
    if (cleancurrentph && cleantargetph) {
      const text = `Total VN (CaO) ${'in'} ${mtHa}*`;

      /* const text = isGreaterTarget
        ? `Total ${mtHa}*` + split
        : 'Kg/ha/yr/' + split; */

      return text;
    }

    return '';
  }, [boardStatusValues, split]);

  const getTotalMtha = useMemo(() => {
    if (cleancurrentph === cleantargetph) {
      const CECSoil = Number(cleansecsmol || 0);
      const NV = nv.length > 0 ? Number(cleannv) : 1;
      const Dose = Number(cleandose) / 100;

      return getP39Value(CECSoil, NV, Dose);
    }

    if (
      cleancurrentph &&
      cleantargetph &&
      cropType &&
      soilType
    ) {
      return calcCurrentPriceByCropNSoil({
        soilType: soilType as SoilTypes,
        cropType: cropType as CropTypes,
        currentPh: cleancurrentph,
        targetPh: cleantargetph,
      });
    }

    return '';
  }, [
    cleancurrentph,
    cleantargetph,
    soilType,
    cropType,
    cleansecsmol,
    cleandose,
  ]);

  const getMtha = useMemo(() => {
    if (getTotalMtha && split && boardStatusValues && nv) {
      const newNV = Number(cleannv) / 100;

      const newVal = isGreaterTarget
        ? parseFloat(getTotalMtha.toString()) / newNV / Number(split)
        : parseFloat(getTotalMtha.toString()) * Number(split);

      return newVal;
    }

    return '';
  }, [getTotalMtha, split, boardStatusValues, cleannv]);

  const [errorCurrent, setErrorCurrent] = useState('');
  const [errorTarget, setErrorTarget] = useState('');
  const [errorTargetSec, setErrorTargetSec] = useState('');
  const [currentSecAuto, setCurrenSecAuto] = useState('');
  const syncedCurrentPh = useRef<number | null>(null);
  const [initialCurrentYield, setInitialCurrentYield] = useState<string>('');
  const [initialTargetPh, setInitialTargetPh] = useState<string>('5,8');

  const disabledState = useMemo(
    () => errorCurrent.length > 0 ||
      errorTarget.length > 0 ||
      Number(cleantargetph) < Number(cleancurrentph) ||
      !!(errorDose && errorDose?.length > 0),
    [
      errorCurrent.length,
      errorTarget.length,
      cleantargetph,
      cleancurrentph,
      errorDose?.length,
    ],
  );

  const handleCropChange = (e: any) => {
    setCropType(e.value);

    const isGrassland = e.value === CropTypes.Grassland;
    const newTargetPh =
      tableTargetStatus[
        isGrassland ? CropTypes.Grassland : CropTypes.OtherCrops
      ][soilType as SoilTypes];

    setTargetPh(getClean(newTargetPh ?? '5,8'));
    setInitialTargetPh(getClean(newTargetPh ?? '5,8'));
  };

  const {
    value: price,
    setValue: setPrice,
    clean: cleanprice,
  } = useMaskedInput({
    defaultValue: '',
    value: boardCropValues?.price ?? '',
    onChangeCallback: changeBoardCropValues('price'),
  });

  const { value: cropGroupYield, setValue: setCropGroupYield } = useMaskedInput(
    {
      defaultValue: '0',
      value: boardCropValues?.cropGroupYield ?? '0',
      onChangeCallback: changeBoardCropValues('cropGroupYield'),
      isDisableClean: true,
    },
  );

  useEffect(() => {
    if (cropType && cropType?.length > 0 && crop) {
      const getCrop = crop?.find((crops) => crops.Crops === cropType);
      const resultPrice =
        metricValue === Metric.metric
          ? String(getCrop?.Price)
          : String(mtTot(Number(getCrop?.Price)));

      const isActuallyChangingCrop = boardCropValues?.crop && boardCropValues.crop !== cropType;
      const hasNoPrice = !boardCropValues?.price;
      
      if (hasNoPrice || isActuallyChangingCrop) {
        setPrice(unMaskInputValue(resultPrice));
        setInitialPrice(unMaskInputValue(resultPrice));
      }
      
      setCropGroupYield(getCrop?.GroupYeildLosses ?? 0);
    }
  }, [cropType, crop]);

  const getYieldGain = useMemo(() => {
    if (cropType && cropType?.length > 0 && crop && boardStatusValues) {
      const getCrop = crop?.find((crops) => crops.Crops === cropType);
      const yeildLossesCurrentTargetPH = yieldPercentHelper(
        getCrop?.GroupYeildLosses || 0,
        +unMaskInput(boardStatusValues.currentPh),
        +unMaskInput(boardStatusValues.targetPh),
      );

      return yeildLossesCurrentTargetPH;
    }

    return '';
  }, [cropType, crop, boardStatusValues]);

  const getYieldGainMtha = useMemo(() => {
    const result =
      getYieldGain && getYieldGain?.length > 0 && cleancurrentyield
        ? String(
          (Number(cleancurrentyield) * (Number(getYieldGain) / 100)).toFixed(
            3,
          ),
        )
        : '0';

    return result;
  }, [getYieldGain, cleancurrentyield]);

  const getYieldGainPrice = useMemo(() => {
    const valueResult = Number(getYieldGainMtha) * Number(cleanprice);

    return getYieldGainMtha && cleanprice ? String(valueResult.toFixed(2)) : '';
  }, [getYieldGainMtha, price]);

  const getErrorOfPrice = useMemo(() => {
    if (Number(cleanprice) < 10) {
      return `${t('minimumPossibleValue')} is 10`;
    }

    if (Number(cleanprice) > 999999) {
      return `${t('maximumPossibleValue')} is 999'999`;
    }

    return null;
  }, [price]);

  useEffect(() => {
    if (Number(cleancurrentph) < 2.99 && currentph.length >= 0) {
      setErrorCurrent(t('minimumPossibleValue') + ' 3');
      setCurrentSec('');

      return;
    }

    if (Number(cleancurrentph) > 8 && currentph.length > 0) {
      setErrorCurrent(t('maximumPossibleValue') + ' 8');
      setCurrentSec('');

      return;
    }

    if (Number(cleancurrentph) < 7.56 && Number(cleancurrentph) > 4.88) {
      setErrorCurrent('');
      const result =
        Math.round(
          (Math.tan((Number(cleancurrentph) - 5.8) / 1.45) * 54 + 70) /
          100 /
          0.05,
        ) * 5;
      const phValue = Number(cleancurrentph);
      const phChanged = syncedCurrentPh.current !== null && syncedCurrentPh.current !== phValue;
      syncedCurrentPh.current = phValue;
      setCurrenSecAuto(String(result) + ' %');
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-expect-error
      setBoardStatusValues((pv) => {
        const savedSec = Number(getClean(pv?.currentSec ?? ''));
        const keepManualSec = !phChanged
          && Boolean(pv?.currentSec)
          && pv?.currentSec !== 'Nicht Relevant'
          && !Number.isNaN(savedSec)
          && savedSec !== result;

        if (keepManualSec) {
          if (pv?.initialCurrentSec === String(result)) {
            return pv;
          }

          return {
            ...pv,
            initialCurrentSec: String(result),
          };
        }

        return {
          ...pv,
          currentSec: `${result} %`,
          initialCurrentSec: String(result),
        };
      });

      return;
    }

    if (errorCurrent.length) {
      setErrorCurrent('');
    }
  }, [currentph, cleancurrentph]);

  useEffect(() => {
    if (
      cleantargetph &&
      Number(cleantargetph) < Number(cleancurrentph) &&
      cleancurrentph
    ) {
      setErrorTarget(
        'La valeur du statut cible ne peut pas être inférieure à la valeur du statut actuel.',
      );

      return;
    }

    if (Number(cleantargetph) < 2.99 && targetph.length >= 0) {
      setErrorTarget(t('minimumPossibleValue') + ' 3');
      setTargetSec('');

      return;
    }

    if (targetph && Number(cleantargetph) > 8) {
      setErrorTarget(t('maximumPossibleValue') + ' 8');
      setTargetSec('');

      return;
    }

    if (
      Number(cleantargetph) < 7.56 &&
      Number(cleantargetph) > 4.88 &&
      Number(cleantargetph) >= Number(cleancurrentph) &&
      currentSec !== 'Nicht Relevant'
    ) {
      setErrorTarget('');
      const result =
        Math.round(
          (Math.tan((Number(cleantargetph) - 5.8) / 1.45) * 54 + 70) /
          100 /
          0.05,
        ) * 5;
      setTargetSec(String(result) + ' %');

      return;
    }

    if (errorTarget.length) {
      setErrorTarget('');
    }
  }, [targetph, currentph, currentSec]);

  useEffect(() => {
    if (Number(cleantargetsec) < 0) {
      setErrorTargetSec(t('minimumPossibleValue') + ' is 0');
    }

    if (Number(cleantargetsec) > 300) {
      setErrorTargetSec(t('maximumPossibleValue') + ' is 300');
    }
  }, [targetSec, targetph]);

  useEffect(() => {
    if (cropType?.length > 0 && crop && !boardCropValues?.currentYield) {
      getCurrentYield(cropType);
    }
  }, [cropType, crop]);

  useEffect(() => {
    const calcSec = (cleanPh: string, setSec: (value: any) => void) => {
      if (
        (Number(cleanPh) <= 4.88 && Number(cleanPh) >= 3) ||
        (Number(cleanPh) <= 8 && Number(cleanPh) > 7.56)
      ) {
        setSec('Nicht Relevant');
        setCurrenSecAuto('Nicht Relevant');
      }
    };

    calcSec(cleancurrentph, (value) => {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-expect-error
      setBoardStatusValues((pv) => ({
        ...pv,
        currentSec: value,
      }));
    });
    calcSec(cleancurrentph, setCurrentSec);
    calcSec(cleantargetph, setTargetSec);
  }, [cleancurrentph, cleantargetph]);

  useEffect(() => {
    if (!soilType && soilSelect && soilSelect.length > 0 && !boardSoilValues?.soilType) {
      setSoilType(soilSelect[0].value);
    }

    if (!cropType && cropSelect && cropSelect.length > 0 && !boardCropValues?.crop) {
      setCropType(cropSelect[0].value);
    }
  }, [soilType, cropSelect, boardCropValues?.crop, boardSoilValues?.soilType]);

  const resetData = () => {
    if (cropSelect && cropSelect.length > 0) {
      setCropType(cropSelect[0].value);
      getCurrentYield(cropSelect[0].value);
    }

    setSoilType(soilSelect[0].value);
    setSecSmol(
      soils?.find((soil) => soil.Soils === soilSelect[0].value)?.CEC.toString() ?? '',
    );
    setCurrentPh('4.89');
    setCurrentSec('30 %');
    setFineDrySoil('20');
    setTargetPh(getClean(tableTargetStatus[CropTypes.OtherCrops][SoilTypes.Sand]));
    setInitialTargetPh(getClean(tableTargetStatus[CropTypes.OtherCrops][SoilTypes.Sand]));
    setDose('100');
    setSplit('1');

    const getCrop = crop?.find((crops) => crops.Crops === cropType);
    const resultPrice =
      metricValue === Metric.metric
        ? String(getCrop?.Price)
        : String(mtTot(Number(getCrop?.Price)));
    setPrice(resultPrice);
    setInitialPrice(resultPrice);
    setCropGroupYield(getCrop?.GroupYeildLosses ?? 0);
  };

  const errorCurrentSecHandle = useMemo(() => {
    if (Number(cleancurrentsec) < 0) {
      return t('minimumPossibleValue') + ' is 0';
    }

    if (Number(cleancurrentsec) > 300) {
      return t('maximumPossibleValue') + ' is 300';
    }

    return '';
  }, [currentSec]);

  const errorSecSmol = useMemo(() => {
    if (Number(cleansecsmol) < 2) {
      return t('minimumPossibleValue') + ' 2';
    }

    if (Number(cleansecsmol) > 200) {
      return t('maximumPossibleValue') + ' 200';
    }

    return null;
  }, [secSmol]);

  const getErrorOfCurrentYield = useMemo(() => {
    if (Number(cleancurrentyield) < 0) {
      return `${t('minimumPossibleValue')} is 0`;
    }

    if (Number(cleancurrentyield) > 1000) {
      return `${t('maximumPossibleValue')} is 1000`;
    }

    return '';
  }, [currentYield]);

  const handleFineDrySoil = useMemo(() => {
    if (Number(cleanfineDrySoil) < 5) {
      return t('minimumPossibleValue') + ' 5';
    }

    if (Number(cleanfineDrySoil) > 50) {
      return t('maximumPossibleValue') + ' 50';
    }

    return null;
  }, [fineDrySoil]);

  const getOptimumValue = useMemo(() => {
    if (boardStatusValues && efficiency?.length) {
      const targetSec = Number(numericValues(boardStatusValues?.targetSec));

      // Filter out items with null CAP values
      const validEfficiency = efficiency.filter((item) => item.CAP !== null);

      if (validEfficiency.length === 0) {
        return '';
      }

      if (targetSec < 40) {
        return String(
          validEfficiency.reduce((acc, loc) => (acc.CAP < loc.CAP ? acc : loc))
            .CAP,
        );
      }

      if (targetSec > 150) {
        return String(
          validEfficiency.reduce((acc, loc) => (acc.CAP > loc.CAP ? acc : loc))
            .CAP,
        );
      }

      return String(
        validEfficiency.reduce((a, b) => Math.abs(b.CAP - targetSec) < Math.abs(a.CAP - targetSec) ? b : a,
        ).CAP,
      );
    }

    return '';
  }, [boardStatusValues, efficiency]);

  useEffect(() => {
    if (soilType && !boardSoilValues?.secsmol) {
      setSecSmol(
        soils?.find((soil) => soil.Soils === soilType)?.CEC.toString() ?? '',
      );
    }
  }, [soilType]);

  /*   const { value: productType } = useMaskedInput({
    defaultValue: '',
    value: boardSoilValues?.productName ?? '',
    onChangeCallback: (value) => {
      changeBoardSoilValues('productName')(value);
      // setInitialNv(String(getProduct?.NV));
    },
    isDisableClean: true,
  }); */

  /* const getSelectedValue = useMemo(() => {
    if (productType?.length > 0 && products) {
      const getProduct = products?.find(
        (product) => product.Products === productType,
      );

      return String(getProduct?.CAP);
    }

    return '';
  }, [productType, products]); */

  const handleNextStep = () => {
    setHandler(() => {
      setBoardStatusValues({
        currentPh: cleancurrentph,
        currentSec: cleancurrentsec,
        targetPh: cleantargetph,
        targetSec: cleantargetsec,
        initialCurrentSec: currentSecAuto,
      });
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-expect-error
      setBoardSoilValues((prevState) => ({
        ...prevState,
        soilType,
        fineDrySoil,
        nv: cleannv,
        selected: '110', // TODO use getSelectedValue()
        totalMtha: String(Number(getTotalMtha)),
        dose: cleandose,
        split,
      }));
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-expect-error
      setBoardCropValues((prevState) => ({
        ...prevState,
        crop: cropType,
      }));

      setStep(BoardSteps.crop);
    });
  };

  useEffect(() => {
    // When active, configure navigation buttons
    // and define the "Next" handler to update board state and trigger status handling
    if (!isActive) {
      return;
    }

    setShowBack(false);
    setShowNext(true);
    setDisabled(disabledState);

    handleNextStep();
  }, [
    isActive,
    disabledState,
    cleancurrentph,
    cleancurrentsec,
    cleantargetph,
    cleantargetsec,
    currentSecAuto,
    soilType,
    cropType,
    fineDrySoil,
    cleandose,
    split,
  ]);

  return {
    // Data
    cropSelect,
    soilSelect,
    soilType,
    setSoilType,
    cropType,
    setCropType,
    currentph,
    setCurrentPh,
    cleancurrentph,
    currentSec,
    setCurrentSec,
    cleancurrentsec,
    targetSec,
    setTargetSec,
    cleantargetsec,
    targetph,
    setTargetPh,
    cleantargetph,
    secSmol,
    setSecSmol,
    cleansecsmol,
    metricValue,
    kg,
    mtHa,
    initialSecSmol,
    fineDrySoil,
    setFineDrySoil,
    cleanfineDrySoil,
    currentYield,
    setCurrentYield,
    cleancurrentyield,
    errorCurrent,
    errorTarget,
    errorTargetSec,
    currentSecAuto,
    initialCurrentYield,
    initialTargetPh,
    disabledState,
    errorCurrentSecHandle,
    errorSecSmol,
    getErrorOfCurrentYield,
    handleFineDrySoil,
    dose,
    setDose,
    cleandose,
    errorDose,
    split,
    setSplit,
    isGreaterTarget,
    getMthaText,
    getMtha,
    getOptimumValue,
    getTotalMtha,

    // crop
    initialPrice,
    getErrorOfPrice,
    getYieldGain,
    getYieldGainPrice,
    cropGroupYield,
    cleanprice,
    getYieldGainMtha,
    price,
    setPrice,

    // Methods
    handleCropChange,
    resetData,
  };
}; 